/**
 * 用户状态管理
 */
import { defineStore } from 'pinia'
import { AuthAPI } from '@/api'
import storage from '@/common/storage'
import router from '@/router'
import { useSubscriptionStore } from '@/stores/subscription'
import { useCartStore } from '@/stores/cart'

const CART_SESSION_KEY = 'cartSessionKey'

export const useUserStore = defineStore('user', {
  state: () => ({
    token: storage.get('token', ''),
    refreshToken: storage.get('refreshToken', ''),
    userInfo: storage.get('userInfo', null),
    isLoggedIn: false,
    cartSessionKey: storage.get(CART_SESSION_KEY, null)
  }),

  getters: {
    /**
     * 是否已认证
     */
    isAuthenticated: (state) => !!state.token && state.isLoggedIn,

    /**
     * 获取用户手机号
     */
    userPhone: (state) => state.userInfo?.phone || '',

    /**
     * 获取用户ID
     */
    userId: (state) => state.userInfo?.id || null
  },

  actions: {
    /**
     * 初始化认证状态
     */
    initAuth() {
      if (this.token) {
        this.isLoggedIn = true
        // 可以在这里验证token有效性
      }
      this.syncCartSession()

      // 确保订阅状态从 localStorage 正确同步到 subscriptionStore
      // subscriptionStore 在 state 初始化时会从 localStorage 读取 subscriptionInfo，
      // 但需要显式触发一次 setSubscriptionInfo 以确保所有 getter 和派生状态正确计算
      const subscriptionStore = useSubscriptionStore()
      if (subscriptionStore.subscriptionInfo) {
        subscriptionStore.setSubscriptionInfo(subscriptionStore.subscriptionInfo)
      }
    },

    /**
     * 发送验证码
     */
    async sendCode(phone) {
      try {
        const result = await AuthAPI.sendVerificationCode(phone)
        return result
      } catch (error) {
        throw error
      }
    },

    /**
     * 手机号登录
     */
    async login(phone, code) {
      try {
        const result = await AuthAPI.phoneLogin(phone, code)
        return this.applyLoginResult(result)
      } catch (error) {
        throw error
      }
    },

    /**
     * CMI免验证码登录
     */
    async cmiLogin(data) {
      try {
        const result = await AuthAPI.cmiLogin(data)
        return this.applyLoginResult(result)
      } catch (error) {
        throw error
      }
    },

    /**
     * 保存登录结果
     */
    applyLoginResult(result) {
        const subscriptionStore = useSubscriptionStore()

        // 保存认证信息
        this.token = result.access_token
        this.refreshToken = result.refresh_token
        const userInfo = result.user_info || result.user || result.userInfo || null
        this.userInfo = userInfo
        this.isLoggedIn = true

        // 持久化存储
        storage.set('token', this.token)
        storage.set('refreshToken', this.refreshToken)
        storage.set('userInfo', this.userInfo)

        // 订阅信息同步
        const subscriptionInfo = result.subscription_info || userInfo?.subscription_info || null
        subscriptionStore.setSubscriptionInfo(subscriptionInfo)
        if (userInfo?.id) {
          subscriptionStore.resetPopupDismissed(userInfo.id)
        }

        // 清空上一次登录的菜单照片缓存和购物车数据
        const newCartSessionKey = this.generateCartSessionKey(userInfo?.id)
        this.cartSessionKey = newCartSessionKey
        storage.set(CART_SESSION_KEY, newCartSessionKey)
        this.clearMenuPhotoCache()
        this.clearCartData(newCartSessionKey)
        this.syncCartSession()

        return result
    },

    /**
     * 刷新Token
     */
    async refreshAccessToken() {
      if (!this.refreshToken) {
        throw new Error('No refresh token available')
      }

      try {
        const result = await AuthAPI.refreshToken(this.refreshToken)

        // 更新token
        this.token = result.access_token
        if (result.refresh_token) {
          this.refreshToken = result.refresh_token
        }

        // 更新存储
        storage.set('token', this.token)
        storage.set('refreshToken', this.refreshToken)

        return result
      } catch (error) {
        // 刷新失败，清除认证信息
        this.logout()
        throw error
      }
    },

    /**
     * 退出登录
     */
    logout() {
      // 清除状态
      this.token = ''
      this.refreshToken = ''
      this.userInfo = null
      this.isLoggedIn = false

      // 清除存储
      storage.remove('token')
      storage.remove('refreshToken')
      storage.remove('userInfo')

      const subscriptionStore = useSubscriptionStore()
      subscriptionStore.setSubscriptionInfo(null)

      // 清空菜单照片缓存和购物车数据
      this.cartSessionKey = null
      storage.remove(CART_SESSION_KEY)
      this.clearMenuPhotoCache()
      this.clearCartData()
      this.syncCartSession()

      // 跳转到登录页
      router.push('/login')
    },

    /**
     * 更新用户信息
     */
    updateUserInfo(userInfo) {
      this.userInfo = { ...this.userInfo, ...userInfo }
      storage.set('userInfo', this.userInfo)
    },

    /**
     * 清除用户数据（不跳转）
     */
    clearUserData() {
      this.token = ''
      this.refreshToken = ''
      this.userInfo = null
      this.isLoggedIn = false

      storage.remove('token')
      storage.remove('refreshToken')
      storage.remove('userInfo')

      const subscriptionStore = useSubscriptionStore()
      subscriptionStore.setSubscriptionInfo(null)

      // 清空菜单照片缓存和购物车数据
      this.cartSessionKey = null
      storage.remove(CART_SESSION_KEY)
      this.clearMenuPhotoCache()
      this.clearCartData()
      this.syncCartSession()
    },

    /**
     * 清空菜单照片缓存
     * 清除所有与菜单照片相关的 sessionStorage 数据
     */
    clearMenuPhotoCache() {
      try {
        // 清除相机页面的临时照片缓存
        sessionStorage.removeItem('camera_multi_photos_temp')
        sessionStorage.removeItem('camera_multi_photos_temp_info')
        sessionStorage.removeItem('camera_multi_photos_temp_emergency')

        // 清除用于上传的照片数据
        sessionStorage.removeItem('menu_photos')

        console.log('菜单照片缓存已清空')
      } catch (error) {
        console.error('清空菜单照片缓存失败:', error)
      }
    },

    /**
     * 清空购物车数据
     * 清除 localStorage 中的购物车持久化数据
     */
    clearCartData(sessionKey = null) {
      try {
        // 先移除持久化数据，避免下一次初始化时读到旧内容
        localStorage.removeItem('cart-storage')

        // 同步清空内存中的购物车状态，防止插件立即写回旧数据
        const cartStore = this.$pinia ? useCartStore(this.$pinia) : useCartStore()
        if (cartStore?.resetCartState) {
          cartStore.resetCartState(sessionKey || null)
        } else if (cartStore) {
          cartStore.clearCart?.()
          cartStore.sessionKey = sessionKey || null
        }
        cartStore?.$persist?.()

        console.log('购物车数据已清空')
      } catch (error) {
        console.error('清空购物车数据失败:', error)
      }
    },

    /**
     * 根据会话标识同步购物车，防止展示其他账号残留数据
     */
    syncCartSession() {
      try {
        const cartStore = this.$pinia ? useCartStore(this.$pinia) : useCartStore()
        const sessionKey = this.cartSessionKey || null
        if (cartStore?.ensureSession) {
          cartStore.ensureSession(sessionKey)
        } else if (!sessionKey) {
          cartStore?.clearCart?.()
          cartStore.sessionKey = null
        }
      } catch (error) {
        console.error('同步购物车归属失败:', error)
      }
    },

    /**
     * 生成新的购物车会话标识，保证每次登录获得全新购物车
     */
    generateCartSessionKey(userId) {
      const uid = userId || 'guest'
      const random = Math.random().toString(36).slice(2, 8)
      return `${uid}-${Date.now()}-${random}`
    }
  }
})
